import { z } from 'zod/v3';
import { EntitySchema } from '../../common/utils/schema/EntitySchema';
import { createPaginationSchema } from '../../common/utils/schema';
import { AccessGroupSchema } from '../../accessGroup';
import { errorMap, zDescription, zName } from '../../common/utils';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './RoleSchema.translations.json';
import { PermissionSchema } from '../../permission/schema/PermissionSchema';

const { addTranslations, t } = i18nNsFactory('role-schema');
addTranslations(translations);

export const BaseRoleSchema = EntitySchema.extend({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
});

export const RoleSchema = BaseRoleSchema.extend({
  permissions: PermissionSchema.array(),
  accessGroups: AccessGroupSchema.array(),
});

export const RoleListSchema = BaseRoleSchema.extend({
  permissions: z.string().nullable(),
  accessGroups: z.string().nullable(),
});

export const RolePageSchema = createPaginationSchema(RoleListSchema);

export const RoleValidFormDataSchema = z
  .object({
    name: zName(errorMap(() => t('validation.name.required'))).min(1),
    description: zDescription().nullable(),
    permissions: z
      .object({
        id: z.number().optional(),
        name: z.string(errorMap(() => t('validation.permission.required'))),
      })
      .array(),
    accessGroups: z
      .object({
        id: z.number().optional(),
        name: z.string(errorMap(() => t('validation.accessGroup.required'))),
      })
      .array(),
  })
  .superRefine((data, ctx) => {
    findDuplicateIndexes(data.permissions).forEach((index) => {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('validation.permission.duplicate'),
        path: ['permissions', index, 'name'],
      });
    });
    findDuplicateIndexes(data.accessGroups).forEach((index) => {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('validation.accessGroup.duplicate'),
        path: ['accessGroups', index, 'name'],
      });
    });
  });

export const RoleMutationSchema = RoleValidFormDataSchema.transform(
  ({ permissions, accessGroups, ...rest }) => ({
    ...rest,
    permissionIds: permissions.map((permission) => permission.id),
    accessGroupIds: accessGroups.map((accessGroup) => accessGroup.id),
  }),
);

const findDuplicateIndexes = <T extends { id?: number }>(
  items: T[],
): number[] => {
  const seen = new Map<number, number[]>();
  items.forEach((item, index) => {
    if (!item.id) return;
    if (!seen.has(item.id)) {
      seen.set(item.id, []);
    }
    seen.get(item.id)?.push(index);
  });

  return Array.from(seen.values())
    .filter((indexes) => indexes.length > 1)
    .flatMap((indexes) => indexes.slice(1));
};
